import React, { useState, useEffect } from "react";
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
} from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import LoginPage from "./components/LoginPage";
import PlatformShell from "./components/PlatformShell";
import OnboardingWizard from './components/onboarding/OnboardingWizard';
import WaitingScreen from "./components/WaitingScreen";
import DisabledScreen from "./components/DisabledScreen";
import { initPlatformApi, checkRegistration } from "./services/platformApi";

function AuthenticatedApp() {
  const { instance, inProgress } = useMsal();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [userState, setUserState] = useState(null);
  // userState: null | { registered, status, role, employeeId }

  useEffect(() => {
    if (instance && inProgress === InteractionStatus.None) {
      initPlatformApi(instance);
      checkUserRegistration();
    }
    // eslint-disable-next-line
  }, [instance, inProgress]);

  const checkUserRegistration = async () => {
    try {
      setLoading(true);
      setError("");
      const result = await checkRegistration();
      setUserState(result);
    } catch (err) {
      const msg = (err.message || "").toLowerCase();
      // If token expired or unauthorized, redirect to login (share token safe in localStorage)
      if (msg.includes("401") || msg.includes("unauthorized") || msg.includes("session expired")) {
        instance.loginRedirect({ scopes: ["User.Read"] }).catch(() => {});
        return;
      }
      setError(err.message || "Could not verify your account. Please try again.");
    } finally {
      setLoading(false);
    }
    
  };

  // Show loader while checking
  if (loading || inProgress !== InteractionStatus.None) {
    return (
      <div className="loader-screen">
        <div className="loader-spinner" />
        <p>Checking your account...</p>
      </div>
    );
  }

  // Error state — registration check failed
  if (error) {
    return (
      <div className="loader-screen">
        <p style={{ color: "#dc2626", maxWidth: 360, textAlign: "center" }}>{error}</p>
        <button className="waiting-logout-btn" onClick={checkUserRegistration}>
          Retry
        </button>
      </div>
    );
  }

  // Not registered → Onboarding Wizard
  if (userState && !userState.registered) {
    return <OnboardingWizard onComplete={checkUserRegistration} />;
  }

  // Registered but PENDING → Waiting Screen
  if (userState && userState.status === "PENDING") {
    return <WaitingScreen />;
  }

  // Registered but DISABLED → Blocked Screen
  if (userState && userState.status === "DISABLED") {
    return <DisabledScreen />;
  }

  // ACTIVE → Dashboard (pass role for admin panel visibility)
  return <PlatformShell userRole={userState?.role} employeeId={userState?.employeeId} />;
}

function App() {
  const { inProgress } = useMsal();

  if (
    inProgress === InteractionStatus.Startup ||
    inProgress === InteractionStatus.HandleRedirect
  ) {
    return (
      <div className="loader-screen">
        <div className="loader-spinner" />
        <p>Securing your session...</p>
      </div>
    );
  }

  return (
    <div className="app">
      <UnauthenticatedTemplate>
        <LoginPage />
      </UnauthenticatedTemplate>
      <AuthenticatedTemplate>
        <AuthenticatedApp />
      </AuthenticatedTemplate>
    </div>
  );
}

export default App;
